import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Trophy } from 'lucide-react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Pagination from '@/components/Pagination';
import { PageLayout } from '../../../components/ui/PageLayout';
import { LoadingState } from '../../../components/AsyncStates';
import TournamentCard from './components/TournamentCard';
import AddTournamentModal from './components/AddTournamentModal';
import EditTournamentModal from './components/EditTournamentModal';
import DeleteTournamentModal from './components/DeleteTournamentModal';
import { useTournaments } from '@/hooks/useTournaments';
import { useTournamentStats } from '@/hooks/useTournamentStats';
import { TournamentStatus } from '@/models/tournament';
import type { TournamentView, TournamentListQuery } from '@/models/tournament';

const TournamentList: React.FC = () => {
  const { t } = useTranslation('tournament');
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({ start: '', end: '' });
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTournament, setSelectedTournament] = useState<TournamentView | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(6);

  // Build query for API
  const query: TournamentListQuery = useMemo(() => ({
    search: searchTerm || undefined,
    status: statusFilter !== '' ? (parseInt(statusFilter) as TournamentStatus) : undefined,
    startDateFrom: dateRange.start || undefined,
    startDateTo: dateRange.end || undefined,
    page: currentPage,
    pageSize: itemsPerPage,
  }), [searchTerm, statusFilter, dateRange.start, dateRange.end, currentPage, itemsPerPage]);

  const { data, loading, error, refetch } = useTournaments(query);
  const { data: stats } = useTournamentStats();

  const tournaments = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;
  const totalPages = data?.totalPages ?? 0;

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, dateRange.start, dateRange.end]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleViewTournament = (tournament: TournamentView) => {
    navigate(`/tournament-management/matches?tournamentId=${tournament.id}`);
  };

  const handleEditTournament = (tournament: TournamentView) => {
    setSelectedTournament(tournament);
    setShowEditModal(true);
  };

  const handleDeleteTournament = (tournament: TournamentView) => {
    setSelectedTournament(tournament);
    setShowDeleteModal(true);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setDateRange({ start: '', end: '' });
  };

  const hasActiveFilters = searchTerm || statusFilter || dateRange.start || dateRange.end;


  return (
    <PageLayout
      title={t('Tournaments')}
      description={t('Manage tournaments, matches, and referee assignments')}
      icon={Trophy}
      accentColor="amber"
      maxWidth="1600px"
      headerActions={
        <Button
          variant="outline"
          onClick={() => setShowAddModal(true)}
          iconName="Plus"
          size="lg"
          data-testid="tournament-add-btn"
        >
          {t('Add Tournament')}
        </Button>
      }
    >
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            {[
              { label: t('Total'), value: stats.totalTournaments, icon: 'Trophy' },
              { label: t('Active'), value: stats.activeTournaments, icon: 'Play' },
              { label: t('Upcoming'), value: stats.upcomingTournaments, icon: 'Calendar' },
              { label: t('Completed'), value: stats.completedTournaments, icon: 'CheckCircle' },
              { label: t('Matches'), value: stats.totalMatches, icon: 'Swords' },
            ].map((card) => (
              <div key={card.label} className="bg-card rounded-lg border border-border p-4 flex flex-col items-center justify-center text-center" data-testid={`stat-card-${card.label.toLowerCase()}`}>
                <span className="text-2xl font-bold text-gray-900">{card.value}</span>
                <span className="text-sm text-muted-foreground mt-1">{card.label}</span>
              </div>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="bg-card rounded-lg shadow-sm border border-border p-6 mb-6" data-testid="tournament-filters">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <Icon name="Filter" size={20} className="me-2" />
              {t('Filters')}
            </h2>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="flex items-center text-sm text-gray-600 hover:text-gray-900"
                data-testid="tournament-clear-filters-btn"
              >
                <Icon name="X" size={14} className="me-1" />
                {t('Clear All')}
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="md:col-span-2">
              <Input
                type="text"
                placeholder={t('Search tournaments, venues...')}
                value={searchTerm}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                className="w-full"
                data-testid="tournament-search-input"
              />
            </div>

            {/* Status Filter */}
            <div>
              <Select
                value={statusFilter}
                onChange={setStatusFilter}
                options={[
                  { value: '', label: t('All Statuses') },
                  { value: '0', label: t('Upcoming') },
                  { value: '1', label: t('Active') },
                  { value: '2', label: t('Completed') },
                  { value: '3', label: t('Cancelled') },
                ]}
                placeholder={t('Status')}
                data-testid="tournament-status-filter"
              />
            </div>

            {/* Date Range */}
            <div className="flex items-center space-x-2 rtl:space-x-reverse">
              <Input
                type="date"
                value={dateRange.start}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                placeholder={t('Start Date')}
                data-testid="tournament-date-start"
              />
              <span className="text-gray-400">-</span>
              <Input
                type="date"
                value={dateRange.end}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                placeholder={t('End Date')}
                data-testid="tournament-date-end"
              />
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="py-12">
            <LoadingState title={t('Loading tournaments...')} size="md" />
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
            <div className="flex items-start">
              <Icon name="AlertCircle" size={20} className="text-red-600 me-3 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-semibold text-red-900 mb-1">{t('Error loading tournaments')}</h3>
                <p className="text-sm text-red-700">{error}</p>
                <Button variant="outline" size="sm" onClick={refetch} className="mt-3" data-testid="tournament-retry-btn">
                  {t('Try Again')}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Results Summary */}
        {!loading && !error && (
          <>
            {/* Tournament Grid */}
            {tournaments.length === 0 ? (
              <div className="bg-card rounded-lg shadow-sm border border-border p-12 text-center" data-testid="tournament-empty-state">
                <Icon name="Search" size={48} className="mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('No tournaments found')}</h3>
                <p className="text-gray-600 mb-6">{t('Try adjusting your filters or search terms')}</p>
                {hasActiveFilters && (
                  <Button onClick={clearFilters} variant="outline" data-testid="tournament-clear-filters-empty-btn">
                    {t('Clear Filters')}
                  </Button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {tournaments.map(tournament => (
                  <TournamentCard
                    key={tournament.id}
                    tournament={tournament}
                    onView={() => handleViewTournament(tournament)}
                    onEdit={() => handleEditTournament(tournament)}
                    onDelete={() => handleDeleteTournament(tournament)}
                  />
                ))}
              </div>
            )}

            {/* Pagination Controls */}
            {totalCount > 0 && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    variant="full"
                    pageSize={itemsPerPage}
                    pageSizeOptions={[6, 12, 18, 24]}
                    onPageSizeChange={(size) => { setItemsPerPage(size); setCurrentPage(1); }}
                    totalCount={totalCount}
                />
            )}
          </>
        )}

      {/* Modals */}
      <AddTournamentModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onCreated={refetch}
      />
      <EditTournamentModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        tournament={selectedTournament}
        onUpdated={refetch}
      />
      <DeleteTournamentModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        tournament={selectedTournament}
        onDeleted={refetch}
      />
    </PageLayout>
  );
};

export default TournamentList;
